import { authFetch } from "@/lib/authFetch";
import { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calendar as CalendarIcon, Clock, Plus, Link2, Loader2, X,
  Check, RefreshCw, AlertCircle, Repeat, ExternalLink, Trash2,
} from "lucide-react";
import { format, parseISO, isSameDay, startOfMonth, endOfMonth } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";

import { Calendar } from "@/components/ui/calendar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Identidade visual por provider — MESMA paleta da tela de Conexões
// (Google cyan, Outlook roxo). Default google p/ retrocompat com eventos
// antigos que não carregam `provider`.
function providerMeta(provider) {
  const isOutlook = String(provider || "google").toLowerCase() === "outlook";
  return isOutlook
    ? {
        label: "Outlook",
        badgeClass: "text-brand-purple border-brand-purple/30 bg-brand-purple/5",
        dotClass: "bg-brand-purple",
        linkLabel: "Abrir no Outlook",
      }
    : {
        label: "Google",
        badgeClass: "text-brand-cyan border-brand-cyan/30 bg-brand-cyan/5",
        dotClass: "bg-brand-cyan",
        linkLabel: "Abrir no Google Calendar",
      };
}

// Sessão UI-2 — AgendaTab
//
// Layout 2 cols: calendário mensal (lg:col-span-2) + ordens do dia
// (lg:col-span-1). Banner GCal acima. "+ Novo Lembrete" right.
//
// Modifiers no DayPicker pra marcar dias com lembrete (purple) ou evento
// Google (cyan). Click no dia carrega ordens do dia (lembretes+eventos
// combinados, sort por hora).

function GoogleBanner({ status, onConnect, onDisconnect, busy }) {
  if (status.loading) {
    return (
      <Card variant="elevated" className="glass-card px-5 py-3.5 flex items-center gap-3">
        <Loader2 className="w-4 h-4 animate-spin text-foreground-muted" />
        <span className="text-caption text-foreground-muted">Verificando conexão…</span>
      </Card>
    );
  }
  if (!status.configured) {
    return (
      <Card variant="elevated" className="glass-card glass-card-amber px-5 py-3.5 flex items-center gap-3">
        <AlertCircle className="w-4 h-4 text-status-warning shrink-0" />
        <span className="text-caption text-foreground-muted">
          Google Calendar — credenciais OAuth não configuradas no backend.
        </span>
      </Card>
    );
  }
  if (!status.connected) {
    return (
      <Card variant="elevated" className="glass-card glow-hover px-5 py-3.5 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-brand-cyan/10 flex items-center justify-center shrink-0">
            <CalendarIcon className="w-4 h-4 text-brand-cyan" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground">Google Calendar</p>
            <p className="text-micro text-foreground-muted">Veja seus compromissos no dashboard.</p>
          </div>
        </div>
        <Button
          variant="primary"
          size="md"
          onClick={onConnect}
          className="bg-brand-cyan/15 border border-brand-cyan/40 text-brand-cyan hover:bg-brand-cyan/25 hover:shadow-glow-cyan"
          data-testid="agenda-gcal-connect"
        >
          <Link2 className="w-4 h-4" />
          Conectar
        </Button>
      </Card>
    );
  }
  return (
    <Card variant="elevated" className="glass-card glass-card-emerald px-5 py-3.5 flex items-center justify-between gap-3 flex-wrap group">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-brand-green/10 flex items-center justify-center">
          <Check className="w-4 h-4 text-brand-green" />
        </div>
        <span className="text-caption text-brand-green/90 font-medium">Google Calendar conectado</span>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={onDisconnect}
        disabled={busy}
        className="opacity-0 group-hover:opacity-100 transition-opacity"
        data-testid="agenda-gcal-disconnect"
      >
        Desconectar
      </Button>
    </Card>
  );
}

function CreateReminderModal({ open, onClose, onSubmit, defaultDate }) {
  const [message, setMessage] = useState("");
  const [date, setDate] = useState(defaultDate || format(new Date(), "yyyy-MM-dd"));
  const [time, setTime] = useState("09:00");
  const [recurrence, setRecurrence] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setMessage("");
      setDate(defaultDate || format(new Date(), "yyyy-MM-dd"));
      setTime("09:00");
      setRecurrence("");
    }
  }, [open, defaultDate]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setSubmitting(true);
    try {
      const datetime = `${date}T${time}:00`;
      await onSubmit({
        message: message.trim(),
        datetime_scheduled: datetime,
        recurrence: recurrence || null,
        priority: "medium",
      });
      onClose();
    } finally { setSubmitting(false); }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-labelledby="reminder-modal-title"
      >
        <motion.div
          initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-surface-overlay border border-subtle rounded-2xl p-6 safe-modal-pb w-full max-w-md"
        >
          <div className="flex items-center justify-between mb-5">
            <h3 id="reminder-modal-title" className="font-display font-semibold text-h2 text-foreground">Novo lembrete</h3>
            <button type="button" onClick={onClose} aria-label="Fechar" className="p-1 rounded-md text-foreground-muted hover:text-foreground hover:bg-surface-card-hover">
              <X className="w-4 h-4" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-caption text-foreground-muted mb-1.5 uppercase" style={{ letterSpacing: "0.08em" }}>Mensagem</label>
              <input
                autoFocus
                type="text" value={message} onChange={(e) => setMessage(e.target.value)}
                placeholder="Ex: Tomar remédio"
                className="w-full px-4 py-3 bg-surface-card border border-subtle rounded-lg text-foreground placeholder-foreground-subtle focus:outline-none focus:border-brand-cyan/50 transition-colors"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-caption text-foreground-muted mb-1.5 uppercase" style={{ letterSpacing: "0.08em" }}>Data</label>
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full px-3 py-3 bg-surface-card border border-subtle rounded-lg text-foreground focus:outline-none focus:border-brand-cyan/50" />
              </div>
              <div>
                <label className="block text-caption text-foreground-muted mb-1.5 uppercase" style={{ letterSpacing: "0.08em" }}>Hora</label>
                <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="w-full px-3 py-3 bg-surface-card border border-subtle rounded-lg text-foreground focus:outline-none focus:border-brand-cyan/50" />
              </div>
            </div>
            <div>
              <label className="block text-caption text-foreground-muted mb-1.5 uppercase" style={{ letterSpacing: "0.08em" }}>Recorrência (opcional)</label>
              <select value={recurrence} onChange={(e) => setRecurrence(e.target.value)} className="w-full px-4 py-3 bg-surface-card border border-subtle rounded-lg text-foreground focus:outline-none focus:border-brand-cyan/50">
                <option value="">Não recorrente</option>
                <option value="daily">Diariamente</option>
                <option value="weekly">Semanalmente</option>
                <option value="monthly">Mensalmente</option>
              </select>
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="ghost" onClick={onClose} className="flex-1">Cancelar</Button>
              <Button type="submit" variant="primary" disabled={!message.trim() || submitting} className="flex-1">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Criar
              </Button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default function AgendaTab() {
  const { user } = useAuth();
  const userId = user?.id || "default_user";
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [month, setMonth] = useState(new Date());
  const [reminders, setReminders] = useState([]);
  const [events, setEvents] = useState([]);
  const [gcalStatus, setGcalStatus] = useState({ loading: true, configured: false, connected: false });
  const [gcalBusy, setGcalBusy] = useState(false);
  const [loadingReminders, setLoadingReminders] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  // Fetch reminders (todos, filtra client-side)
  const fetchReminders = useCallback(async () => {
    try {
      const r = await authFetch(`${API_URL}/api/reminders?include_completed=false`);
      if (r.ok) {
        const data = await r.json();
        const list = Array.isArray(data?.reminders) ? data.reminders
                    : Array.isArray(data) ? data : [];
        const mine = list.filter(rem => !rem.userId || rem.userId === userId);
        setReminders(mine);
      }
    } catch (_) { /* silencioso */ }
    finally { setLoadingReminders(false); }
  }, [userId]);

  // Fetch GCal status
  const fetchGcalStatus = useCallback(async () => {
    setGcalStatus(prev => ({ ...prev, loading: true }));
    try {
      const r = await authFetch(`${API_URL}/api/auth/google/status?user_id=${encodeURIComponent(userId)}`);
      if (r.ok) {
        const data = await r.json();
        setGcalStatus({ loading: false, configured: !!data.configured, connected: !!data.connected });
      } else {
        setGcalStatus({ loading: false, configured: false, connected: false });
      }
    } catch (_) {
      setGcalStatus({ loading: false, configured: false, connected: false });
    }
  }, [userId]);

  // Fetch events do mês
  const fetchEvents = useCallback(async () => {
    if (!gcalStatus.connected) { setEvents([]); return; }
    const start = format(startOfMonth(month), "yyyy-MM-dd");
    const end = format(endOfMonth(month), "yyyy-MM-dd");
    try {
      const r = await authFetch(`${API_URL}/api/calendar/events?user_id=${encodeURIComponent(userId)}&start=${start}&end=${end}`);
      if (r.ok) {
        const data = await r.json();
        setEvents(Array.isArray(data?.events) ? data.events : []);
      }
    } catch (_) { setEvents([]); }
  }, [userId, month, gcalStatus.connected]);

  useEffect(() => { fetchReminders(); fetchGcalStatus(); }, [fetchReminders, fetchGcalStatus]);
  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  // OAuth
  const handleConnect = async () => {
    setGcalBusy(true);
    try {
      const r = await authFetch(`${API_URL}/api/auth/google/start?user_id=${encodeURIComponent(userId)}`);
      if (r.ok) {
        const data = await r.json();
        if (data.authorization_url) window.location.href = data.authorization_url;
      } else {
        toast.error("Falha ao iniciar OAuth");
      }
    } finally { setGcalBusy(false); }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Desconectar do Google Calendar?")) return;
    setGcalBusy(true);
    try {
      const r = await authFetch(`${API_URL}/api/auth/google/disconnect?user_id=${encodeURIComponent(userId)}`, { method: "POST" });
      if (r.ok) { toast.success("Desconectado"); await fetchGcalStatus(); setEvents([]); }
    } finally { setGcalBusy(false); }
  };

  // Reminders CRUD
  const handleCreate = async (data) => {
    try {
      const r = await authFetch(`${API_URL}/api/reminders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...data, ...(userId ? { user_id: userId } : {}) }),
      });
      if (r.ok) { toast.success("Lembrete criado"); await fetchReminders(); }
      else toast.error("Erro ao criar lembrete");
    } catch (_) { toast.error("Erro ao criar lembrete"); }
  };

  const handleComplete = async (rem) => {
    try {
      const r = await authFetch(`${API_URL}/api/reminders/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: rem.id }),
      });
      if (r.ok) { setReminders(prev => prev.filter(x => x.id !== rem.id)); toast.success("Concluído"); }
    } catch (_) { /* ignore */ }
  };

  const handleDelete = async (rem) => {
    if (!window.confirm("Excluir este lembrete?")) return;
    try {
      const r = await authFetch(`${API_URL}/api/reminders/${rem.id}`, { method: "DELETE" });
      if (r.ok) { setReminders(prev => prev.filter(x => x.id !== rem.id)); toast.success("Excluído"); }
    } catch (_) { /* ignore */ }
  };

  // Modifiers do DayPicker — datas com lembrete OU evento
  const reminderDates = useMemo(() => reminders
    .map(r => { try { return parseISO(r.datetime_scheduled); } catch { return null; } })
    .filter(Boolean), [reminders]);
  const eventDates = useMemo(() => events
    .map(e => { try { return parseISO(e.start); } catch { return null; } })
    .filter(Boolean), [events]);

  // Items do dia selecionado (reminders + events ordenados por hora)
  const dayItems = useMemo(() => {
    const remOfDay = reminders
      .map(r => {
        let d; try { d = parseISO(r.datetime_scheduled); } catch { return null; }
        return isSameDay(d, selectedDate) ? { ...r, _source: "reminder", _date: d } : null;
      })
      .filter(Boolean);
    const evOfDay = events
      .map(e => {
        let d; try { d = parseISO(e.start); } catch { return null; }
        return isSameDay(d, selectedDate) ? { ...e, _source: "calendar", _date: d } : null;
      })
      .filter(Boolean);
    return [...remOfDay, ...evOfDay].sort((a, b) => a._date - b._date);
  }, [reminders, events, selectedDate]);

  // Multi-conta: só mostra a identificação de conta (apelido + e-mail) quando
  // há ≥2 contas distintas entre TODOS os eventos — com 1 conta a UI não
  // polui com info redundante.
  const showAccount = useMemo(() => {
    const accts = new Set(
      events.map(e => e.account_email).filter(Boolean)
    );
    return accts.size >= 2;
  }, [events]);

  // Providers presentes nos eventos do dia — alimenta a legenda do rodapé.
  const providersInDay = useMemo(() => {
    const set = new Set(
      dayItems
        .filter(it => it._source === "calendar")
        .map(it => String(it.provider || "google").toLowerCase())
    );
    return [...set];
  }, [dayItems]);

  const dateLabel = format(selectedDate, "EEEE, dd 'de' MMMM", { locale: ptBR }).replace(/^./, c => c.toUpperCase());

  return (
    <div className="space-y-6">
      <GoogleBanner
        status={gcalStatus}
        onConnect={handleConnect}
        onDisconnect={handleDisconnect}
        busy={gcalBusy}
      />

      <div className="flex justify-end">
        <Button
          variant="primary"
          size="md"
          onClick={() => setModalOpen(true)}
          className="bg-brand-cyan/15 border border-brand-cyan/40 text-brand-cyan hover:bg-brand-cyan/25 hover:shadow-glow-cyan"
          data-testid="reminder-create-btn"
        >
          <Plus className="w-4 h-4" />
          Novo lembrete
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ESQUERDA — calendário */}
        <Card variant="elevated" className="glass-card glow-hover lg:col-span-2 p-4 md:p-5" data-testid="agenda-calendar">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={(d) => d && setSelectedDate(d)}
            month={month}
            onMonthChange={setMonth}
            locale={ptBR}
            modifiers={{ hasReminder: reminderDates, hasEvent: eventDates }}
            modifiersClassNames={{
              hasReminder: "after:content-[''] after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:w-1 after:h-1 after:rounded-full after:bg-brand-purple",
              hasEvent: "before:content-[''] before:absolute before:bottom-1 before:left-1/2 before:-translate-x-1/2 before:translate-x-1.5 before:w-1 before:h-1 before:rounded-full before:bg-brand-cyan",
            }}
            className="w-full"
            classNames={{
              months: "flex w-full",
              month: "w-full space-y-4",
              caption_label: "text-base font-medium text-foreground capitalize",
              head_cell: "text-foreground-subtle uppercase text-micro w-full text-center py-2",
              row: "grid grid-cols-7 gap-1",
              head_row: "grid grid-cols-7 gap-1",
              cell: "relative flex items-center justify-center",
              day: "h-10 w-10 rounded-md text-foreground hover:bg-surface-card-hover transition-colors",
              day_today: "bg-brand-cyan/10 text-brand-cyan font-semibold",
              day_selected: "bg-brand-green/20 text-brand-green ring-1 ring-brand-green/40 hover:bg-brand-green/25",
              day_outside: "text-foreground-disabled opacity-50",
            }}
          />
        </Card>

        {/* DIREITA — Ordens do dia */}
        <Card variant="elevated" className="glass-card glow-hover p-5 md:p-6" data-testid="agenda-orders">
          <div className="mb-5">
            <span
              className="text-caption font-semibold uppercase text-brand-cyan/80"
              style={{ letterSpacing: "0.12em" }}
            >
              Agenda
            </span>
            <h3 className="font-display font-semibold text-h3 text-foreground mt-1">Ordens do dia</h3>
            <p className="text-caption text-foreground-muted mt-0.5" data-testid="agenda-day-label">{dateLabel}</p>
          </div>

          {loadingReminders ? (
            <div className="flex items-center gap-2 text-foreground-muted text-caption">
              <Loader2 className="w-4 h-4 animate-spin" /> Carregando…
            </div>
          ) : dayItems.length === 0 ? (
            <div className="flex flex-col items-start gap-3 py-4">
              <div className="w-12 h-12 rounded-full bg-brand-cyan/10 flex items-center justify-center">
                <CalendarIcon className="w-5 h-5 text-brand-cyan" />
              </div>
              <h4 className="font-display font-semibold text-foreground text-h3">Dia livre</h4>
              <p className="text-caption text-foreground-muted">
                Sem compromissos. Quer aproveitar pra planejar a semana?
              </p>
            </div>
          ) : (
            <ul className="space-y-2" data-testid="agenda-day-items">
              {dayItems.map((it, idx) => {
                const time = format(it._date, "HH:mm", { locale: ptBR });
                const isReminder = it._source === "reminder";
                const recurring = !!it.recurrence;
                const meta = isReminder ? null : providerMeta(it.provider);
                const acctName = it.connection_label || it.account_email;
                return (
                  <li key={`${it._source}:${it.id || idx}`}>
                    <div className="group flex items-start gap-3 px-3 py-3 rounded-lg hover:bg-surface-card-hover transition-colors">
                      <div className="w-14 shrink-0">
                        <div className="inline-flex items-center gap-1 text-brand-green font-mono text-sm tabular-nums">
                          <Clock className="w-3.5 h-3.5 opacity-70" />
                          {time}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {isReminder ? it.message : it.summary || "(sem título)"}
                        </p>
                        {/* Identificação da conta de origem (multi-conta) —
                            apelido + e-mail, igual à tela de Conexões. */}
                        {!isReminder && showAccount && acctName && (
                          <p className="text-caption text-foreground-subtle truncate">
                            {it.connection_label && it.account_email
                              ? `${it.connection_label} · ${it.account_email}`
                              : acctName}
                          </p>
                        )}
                        <div className="mt-0.5 flex items-center gap-2 flex-wrap">
                          {isReminder ? (
                            <Badge variant="outline" className="text-brand-purple border-brand-purple/30 bg-brand-purple/5">
                              Lembrete
                            </Badge>
                          ) : (
                            <Badge variant="outline" className={meta.badgeClass}>
                              {meta.label}
                            </Badge>
                          )}
                          {recurring && (
                            <Badge variant="outline" className="text-foreground-muted">
                              <Repeat className="w-3 h-3 mr-1" />
                              {it.recurrence === "daily" ? "Diário"
                                : it.recurrence === "weekly" ? "Semanal"
                                : it.recurrence === "monthly" ? "Mensal" : it.recurrence}
                            </Badge>
                          )}
                        </div>
                      </div>
                      {isReminder && (
                        <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            type="button"
                            aria-label="Concluir"
                            onClick={() => handleComplete(it)}
                            className="p-1.5 rounded-md text-foreground-muted hover:text-brand-green hover:bg-brand-green/10"
                          >
                            <Check className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            aria-label="Excluir"
                            onClick={() => handleDelete(it)}
                            className="p-1.5 rounded-md text-foreground-muted hover:text-status-error hover:bg-status-error/10"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                      {!isReminder && it.html_link && (
                        <a
                          href={it.html_link} target="_blank" rel="noopener noreferrer"
                          aria-label={meta.linkLabel}
                          className="p-1.5 text-foreground-subtle hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          <div className="mt-4 pt-4 border-t border-subtle flex items-center justify-between text-micro text-foreground-subtle">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-brand-purple" /> Lembrete
            </span>
            {providersInDay.map((p) => {
              const m = providerMeta(p);
              return (
                <span key={p} className="inline-flex items-center gap-1.5">
                  <span className={`w-2 h-2 rounded-full ${m.dotClass}`} /> {m.label}
                </span>
              );
            })}
            <button
              type="button"
              onClick={() => { fetchReminders(); fetchEvents(); }}
              className="inline-flex items-center gap-1 hover:text-brand-cyan transition-colors"
              aria-label="Atualizar"
            >
              <RefreshCw className="w-3 h-3" />
              Atualizar
            </button>
          </div>
        </Card>
      </div>

      <CreateReminderModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleCreate}
        defaultDate={format(selectedDate, "yyyy-MM-dd")}
      />
    </div>
  );
}
